import { json, LoaderFunction, MetaFunction } from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { Container } from '~/components/Container';
import { Layout } from '~/components/Layout';
import { PageSubHeading } from '~/components/PageSubHeading';
import { PedalList } from '~/components/PedalList';
import { ReadMoreButton } from '~/components/ReadMoreButton';
import { db } from '~/lib/db.server';
import { getCacheControlHeader } from '~/lib/http.server';
import { getSimilarPedals, Pedal } from '~/lib/pgvector.server';
import { getImagePublicUrl } from '~/lib/supabase.server';
import { appendMetaTitleSuffix } from '~/lib/utils/meta';
import { AffiliateLinks } from '~/routes/pedals.$pedalId/AffiliateLinks';
import { PedalDetail } from '~/routes/pedals.$pedalId/PedalDetail';


export const loader: LoaderFunction = async ({ request, params }) => {
  const { pedalId } = params;

  const pedal = await db.pedal.findUnique({
    include: {
      brand: true,
      images: {
        orderBy: {
          order: 'asc'
        }
      },
      youtubes: {
        orderBy: {
          order: 'asc'
        }
      },
      externalLinks: {
        orderBy: {
          order: 'asc'
        }
      },
      tags: {
        include: {
          tag: true,
        },
      }
    },
    where: {
      id: pedalId,
    },
  });

  if (!pedal) {
    throw new Response(null, {
      status: 404,
      statusText: 'Not Found',
    });
  }

  const imageUrlPrefix = getImagePublicUrl(request);

  const sameBrandPedals = await getSimilarPedals({
    pedalId: pedal.id,
    limit: 9,
    brandId: pedal.brandId
  });

  let sameTagPedals: Pedal[] = [];
  if (pedal.tags.length > 0) {
    sameTagPedals = await getSimilarPedals({
      pedalId: pedal.id,
      limit: 9,
      tagId: pedal.tags[0].tagId,
      excludeBrandId: pedal.brandId
    });
  }

  return json(
    {
      pedal,
      imageUrlPrefix,
      sameBrandPedals,
      sameTagPedals,
      amazonAssociateId: process.env.AMAZON_ASSOCIATE_ID || '',
      rakutenAffiliateUrl: process.env.RAKUTEN_AFFILIATE_URL || ''
    },
    {
      headers: {
        ...getCacheControlHeader()
      }
    }
  );
};

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const pedal = data.pedal;
  return [
    { title: appendMetaTitleSuffix(`${pedal.name} | ${pedal.brand.name}`) },
    {
      name: 'description',
      content: `${pedal.description}`,
    },
  ];
};

const PedalRoute = () => {
  const {
    pedal, imageUrlPrefix, sameBrandPedals,
    sameTagPedals, amazonAssociateId, rakutenAffiliateUrl,
  } = useLoaderData<typeof loader>();

  return (
    <Layout>
      <Container>
        <div className={'mt-4'}>
          <PedalDetail
            pedal={pedal}
            imageUrlPrefix={imageUrlPrefix}
            amazonAssociateId={amazonAssociateId}
            rakutenAffiliateUrl={rakutenAffiliateUrl}
          />
        </div>

        {pedal.youtubes.length > 0 && (
          <>
            <div className="mt-12">
              {/* <PageSubHeading>
                動画
              </PageSubHeading> */}
            </div>
            <div className={'grid lg:grid-cols-3 md:grid-cols-2 sm:grid-cols-1 gap-6 mt-3'}>
              {pedal.youtubes.map((youtube: { videoId: string; title: string; description: string; }) => (
                <div key={youtube.videoId}>
                  <div className={'youtube'}>
                    <iframe
                      width="560"
                      height="315"
                      src={`https://www.youtube.com/embed/${youtube.videoId}`}
                      title="YouTube video player"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    ></iframe>
                  </div>
                  <div className={'text-sm my-2'} dangerouslySetInnerHTML={{ __html: youtube.title }} />
                  <div className={'text-gray-400 text-[12px]'}>
                    {youtube.description}
                  </div>
                </div>
              ))}
            </div>

            <div className='mt-12'>
              <AffiliateLinks
                pedal={pedal}
                amazonAssociateId={amazonAssociateId}
                rakutenAffiliateUrl={rakutenAffiliateUrl}
              />
            </div>

          </>
        )}

        {sameBrandPedals.length > 0 && (
          <>
            <div className="mt-20">
              <PageSubHeading>
                他の {pedal.brand.name} のエフェクター
              </PageSubHeading>
            </div>
            <div className={'grid lg:grid-cols-3 md:grid-cols-1 gap-6 mt-3'}>
              <PedalList pedals={sameBrandPedals} imageUrlPrefix={imageUrlPrefix} />
            </div>

            <div className='mt-10'>
              <ReadMoreButton to={`/brands/${pedal.brand.id}`} label={`${pedal.brand.name}のエフェクターをもっとみる`} />
            </div>
          </>
        )}


        {sameTagPedals.length > 0 && (
          <>
            <div className="mt-20">
              <PageSubHeading>
                他の #{pedal.tags[0].tag.tag}
              </PageSubHeading>
            </div>
            <div className={'grid lg:grid-cols-3 md:grid-cols-1 gap-6 mt-3'}>
              <PedalList pedals={sameTagPedals} imageUrlPrefix={imageUrlPrefix} />
            </div>

            <div className='mt-10'>
              <ReadMoreButton to={`/pedals?tags=${pedal.tags[0].tag.id}`} label={`#${pedal.tags[0].tag.tag} をもっとみる`} />
            </div>

          </>
        )}

      </Container>
    </Layout>
  );
};

export default PedalRoute;
